import { ImgHTMLAttributes, useEffect, useMemo, useState } from 'react';
import { foundationSemantic, foundationSpacingTokens, foundationTypographyTokens } from '../../foundation';
import { cx } from './utils';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | number;

interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  name?: string;
  size?: AvatarSize;
  fallback?: string;
  shape?: 'circle' | 'rounded';
}

const sizeClasses: Record<Exclude<AvatarSize, number>, string> = {
  xs: `${foundationSpacingTokens.avatar.xs} ${foundationTypographyTokens.avatar.xs}`,
  sm: `${foundationSpacingTokens.avatar.sm} ${foundationTypographyTokens.avatar.sm}`,
  md: `${foundationSpacingTokens.avatar.md} ${foundationTypographyTokens.avatar.md}`,
  lg: `${foundationSpacingTokens.avatar.lg} ${foundationTypographyTokens.avatar.lg}`
};

function getInitials(name?: string): string {
  if (!name) {
    return '?';
  }

  const parts = name
    .split(' ')
    .map(part => part.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) {
    return '?';
  }

  return parts.map(part => part[0]?.toUpperCase() ?? '').join('');
}

function getColorClass(seed: string): string {
  const hash = Array.from(seed).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return foundationSemantic.avatar.fallbackPalette[hash % foundationSemantic.avatar.fallbackPalette.length];
}

export default function Avatar({
  name,
  size = 'md',
  fallback,
  shape = 'circle',
  className,
  src,
  alt,
  ...imgProps
}: AvatarProps) {
  const [hasImageError, setHasImageError] = useState(false);
  const initials = fallback || getInitials(name);
  const colorClass = useMemo(() => getColorClass(name || initials), [name, initials]);
  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';
  const sizeClass = typeof size === 'number' ? '' : sizeClasses[size];
  const hasImage = Boolean(src) && !hasImageError;

  useEffect(() => {
    setHasImageError(false);
  }, [src]);

  if (hasImage) {
    return (
      <img
        src={src}
        alt={alt ?? name ?? 'Avatar'}
        className={cx('object-cover', shapeClass, sizeClass, className)}
        style={typeof size === 'number' ? { width: size, height: size } : undefined}
        referrerPolicy="no-referrer"
        onError={(event) => {
          setHasImageError(true);
          imgProps.onError?.(event);
        }}
        {...imgProps}
      />
    );
  }

  return (
    <div
      className={cx(
        `inline-flex items-center justify-center font-semibold select-none ${foundationSemantic.avatar.text}`,
        colorClass,
        shapeClass,
        sizeClass,
        className
      )}
      style={typeof size === 'number' ? { width: size, height: size } : undefined}
      aria-label={alt ?? name ?? 'Avatar'}
      role="img"
    >
      {initials}
    </div>
  );
}

export type { AvatarProps, AvatarSize };
